import { faTurnRight } from "@fortawesome/pro-solid-svg-icons/faTurnRight";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Badge, Button, Flex, Text } from "@mantine/core";
import { type ReactNode, useMemo } from "react";
import { twMerge } from "tailwind-merge";

import { UnpublishedChangesIndicator } from "@/components/unpublished-changes-indicator";
import { useSlimScreenerDetailsQuery } from "@/hooks/queries/screener-builder.queries";
import { useScreenerParamsWithNewCheck } from "@/hooks/screener-builder-hooks";
import { DerivedScreenerStatus } from "@/types/screeners";
import { isUnpublishedStatus } from "@/utils/screener-builder";

interface TableHeaderRightContentProps {
  menu: ReactNode;
  onPublishClick?: () => void;
}

const BADGE_CLASSNAMES = [
  "font-bold",
  "h-6",
  "px-3",
  "py-1",
  "text-xs",
  "uppercase",
];

export const TableHeaderRightContent = ({
  menu,
  onPublishClick,
}: TableHeaderRightContentProps) => {
  const [screenerId, idIsNew] = useScreenerParamsWithNewCheck();
  const { data: slimScreenerDetails } = useSlimScreenerDetailsQuery(
    screenerId,
    { enabled: !idIsNew },
  );
  const displayStatus = useMemo(
    () =>
      idIsNew
        ? DerivedScreenerStatus.Draft
        : (slimScreenerDetails?.derivedStatus ?? null),
    [idIsNew, slimScreenerDetails],
  );
  const badgeProps = useMemo(() => {
    switch (displayStatus) {
      case DerivedScreenerStatus.Draft:
        return {
          c: "gray.7",
          className: twMerge(BADGE_CLASSNAMES.concat("border-uw-gray-7")),
          color: "white",
          variant: "outline",
        };
      case DerivedScreenerStatus.Live:
        return {
          c: "#005648",
          color: "mint-green.1",
        };
      case DerivedScreenerStatus.Mixed:
        return {
          c: "#457069",
          color: "#EBFAF7",
        };
      case DerivedScreenerStatus.Paused:
        return {
          c: "gray.7",
          color: "gray.0",
        };
    }
  }, [displayStatus]);
  const hasUnpublishedChanges = useMemo(
    () =>
      !!slimScreenerDetails?.status &&
      isUnpublishedStatus(slimScreenerDetails.status),
    [slimScreenerDetails],
  );

  return (
    <Flex align="center" gap={18}>
      {hasUnpublishedChanges && (
        <>
          <Flex align="center">
            <UnpublishedChangesIndicator showTooltip={false} />
            <Text className="ml-2" size="sm">
              Unpublished changes
            </Text>
          </Flex>
          {onPublishClick && (
            <Button
              onClick={onPublishClick}
              rightSection={<FontAwesomeIcon icon={faTurnRight} />}
              size="compact-sm"
            >
              Publish
            </Button>
          )}
        </>
      )}
      {badgeProps && (
        <Badge className={twMerge(BADGE_CLASSNAMES)} {...badgeProps}>
          {displayStatus}
        </Badge>
      )}
      {menu}
    </Flex>
  );
};
